import { Archive, ArchiveRestore, Send } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ResourceList } from '@/components/resource-list';
import { Button } from '@/components/ui/button';
import { Select, Textarea } from '@/components/ui/form';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/layout';
import { formatDateTime, timeAgo } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useAction, useItem } from '@/lib/resources';
import type { Report } from '@/lib/types';

export function ReportsPage() {
  return (
    <ResourceList<Report>
      title={t('Reports')}
      subtitle={t('Feedback sent from the app. Replying notifies the passenger.')}
      resource="reports"
      basePath="/reports"
      canCreate={false}
      initial={{ is_active: 'true' }}
      filters={(set, v) => (
        <>
          <Select
            className="w-40"
            value={(v.is_active as string) ?? 'true'}
            onChange={(e) => set('is_active', e.target.value)}
          >
            <option value="true">{t('Open')}</option>
            <option value="false">{t('Archived')}</option>
            <option value="">{t('All')}</option>
          </Select>
          <Select
            className="w-40"
            value={(v.replied as string) ?? ''}
            onChange={(e) => set('replied', e.target.value)}
          >
            <option value="">{t('Any reply state')}</option>
            <option value="false">{t('Not replied')}</option>
            <option value="true">{t('Replied')}</option>
          </Select>
        </>
      )}
      columns={[
        {
          header: t('Report'),
          cell: (r) => (
            <div>
              <div className="line-clamp-2 max-w-xl">{r.description}</div>
              <div className="text-xs text-slate-500">{r.user_name || r.user_email || r.user_id}</div>
            </div>
          ),
        },
        {
          header: t('Received'),
          cell: (r) => <span title={formatDateTime(r.created_at)}>{timeAgo(r.created_at)}</span>,
        },
        {
          header: t('State'),
          cell: (r) =>
            !r.is_active ? (
              <Badge>{t('Archived')}</Badge>
            ) : r.reply ? (
              <Badge tone="green">{r.reply.is_read ? t('Reply read') : t('Replied')}</Badge>
            ) : (
              <Badge tone="amber">{t('Needs reply')}</Badge>
            ),
        },
      ]}
    />
  );
}

export function ReportDetailPage() {
  const { id } = useParams();
  const item = useItem<Report>('reports', id);
  const [text, setText] = useState('');
  const reply = useAction<{ pushed: boolean }>(`/reports/${id}/reply`, ['reports'], t('Reply sent'));
  const archive = useAction(`/reports/${id}/archive`, ['reports'], t('Archived'));
  const restore = useAction(`/reports/${id}/restore`, ['reports'], t('Restored'));
  if (item.isLoading || !item.data) return <Spinner />;
  const r = item.data;
  return (
    <>
      <PageHeader
        title={t('Report')}
        subtitle={formatDateTime(r.created_at)}
        back="/reports"
        actions={
          r.is_active ? (
            <Button
              variant="secondary"
              icon={<Archive className="size-4" />}
              loading={archive.isPending}
              onClick={() => archive.mutate(undefined)}
            >
              {t('Archive')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              icon={<ArchiveRestore className="size-4" />}
              loading={restore.isPending}
              onClick={() => restore.mutate(undefined)}
            >
              {t('Restore')}
            </Button>
          )
        }
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Card title={t('Message')}>
            <p className="text-sm whitespace-pre-wrap">{r.description}</p>
          </Card>
          <Card title={r.reply ? t('Reply') : t('Write a reply')}>
            {r.reply && (
              <div className="mb-4 rounded-md bg-brand-50 p-3 text-sm">
                <p className="whitespace-pre-wrap">{r.reply.description}</p>
                <p className="mt-2 text-xs text-slate-500">
                  {r.reply.reply_user_email} · {formatDateTime(r.reply.created_at)} ·{' '}
                  {r.reply.is_read ? t('read by the passenger') : t('not read yet')}
                </p>
              </div>
            )}
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={r.reply ? t('Replace the reply…') : t('Your reply to the passenger…')}
              className="min-h-28"
            />
            <div className="mt-3 flex justify-end">
              <Button
                icon={<Send className="size-4" />}
                loading={reply.isPending}
                disabled={!text.trim()}
                onClick={() => reply.mutate({ description: text }, { onSuccess: () => setText('') })}
              >
                {t('Send reply')}
              </Button>
            </div>
            {reply.data && !reply.data.pushed && (
              <p className="mt-2 text-xs text-amber-700">
                {t(
                  'Saved. The passenger has no push token, so they will see it next time they open their reports.',
                )}
              </p>
            )}
          </Card>
        </div>
        <Card title={t('Passenger')}>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-xs text-slate-500">{t('Name')}</dt>
              <dd>{r.user?.fullname || r.user_name || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">{t('E-mail')}</dt>
              <dd>{r.user?.email || r.user_email || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">{t('Phone')}</dt>
              <dd>{r.user?.phone_no || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">{t('Signed in with')}</dt>
              <dd>{r.user?.sign_in_provider || '—'}</dd>
            </div>
          </dl>
        </Card>
      </div>
    </>
  );
}
